import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Input, InputNumber, Spin } from 'antd';
import documentService from '../../../services/documentService';

const PAGE_SIZE = 20;

/**
 * Vehicle picker backed by the fleet, with every detail still typeable by hand
 * so a vehicle that isn't on the fleet yet doesn't block the document.
 */
const AssetPickerField = ({ value, onChange, companyId, disabled = false }) => {
  const [search, setSearch] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [fleet, setFleet] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Debounce search
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch((search ?? '').trim());
    }, 500);
    return () => window.clearTimeout(handle);
  }, [search]);

  const requestRef = useRef(0);

  const load = useCallback(
    async (nextPageIndex, { append }) => {
      const ticket = (requestRef.current += 1);
      setLoading(true);

      try {
        const page = await documentService.getCompanyFleet(companyId, {
          pageSize: PAGE_SIZE,
          pageIndex: nextPageIndex,
          searchTerm: debouncedSearch,
        });

        if (ticket !== requestRef.current) return;

        setFleet((previous) => (append ? [...previous, ...page.items] : page.items));
        setPageIndex(page.currentPage ?? nextPageIndex);
        setHasMore(!!page.hasNextPage);
      } catch (error) {
        // Non-fatal: the fields below are still typeable by hand.
        console.warn('Failed to load company fleet for asset picker', error);
        if (ticket === requestRef.current && !append) setFleet([]);
      } finally {
        if (ticket === requestRef.current) setLoading(false);
      }
    },
    [companyId, debouncedSearch]
  );

  useEffect(() => {
    if (!companyId || !debouncedSearch) {
      requestRef.current += 1;
      setFleet([]);
      setHasMore(false);
      setLoading(false);
      return;
    }

    load(0, { append: false });
  }, [companyId, debouncedSearch, load]);

  const current = value ?? {};

  const patch = (changes) => onChange?.({ ...current, ...changes });

  const options = useMemo(
    () =>
      fleet.map((vehicle) => ({
        value: vehicle.id,
        label: (
          <div>
            <div className="asset-picker__option-title">
              {[vehicle.registration, [vehicle.make, vehicle.model].filter(Boolean).join(' ')]
                .filter(Boolean)
                .join(' - ')}
              {vehicle.year ? ` (${vehicle.year})` : ''}
            </div>
            <div className="asset-picker__option-meta">
              {vehicle.fleet && <span>Fleet: {vehicle.fleet}</span>}
              {vehicle.fleet && vehicle.vin && <span> • </span>}
              {vehicle.vin && <span>VIN: {vehicle.vin}</span>}
            </div>
          </div>
        ),
      })),
    [fleet]
  );

  const handleSearchChange = (text) => {
    if (fleet.some((vehicle) => vehicle.id === text)) return;
    setSearch(text);
  };

  const handleDropdownScroll = (event) => {
    const { currentTarget } = event;
    if (
      currentTarget.scrollTop + currentTarget.offsetHeight >= currentTarget.scrollHeight - 20 &&
      hasMore &&
      !loading
    ) {
      load(pageIndex + 1, { append: true });
    }
  };

  const emptyText = () => {
    if (loading) return <Spin size="small" />;
    if (!debouncedSearch) return 'Type to search';
    return 'No vehicles found';
  };

  const handleSelect = (vehicleId) => {
    const vehicle = fleet.find((entry) => entry.id === vehicleId);
    if (!vehicle) return;

    setSearch(null);
    patch({
      vehicleId: vehicle.id,
      registration: vehicle.registration || '',
      fleetNo: vehicle.fleet || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      vin: vehicle.vin || '',
      year: vehicle.year || '',
    });
  };

  return (
    <div className="asset-picker">
      {!companyId && (
        <div className="asset-picker__notice">
          Choose a company first to search its fleet. Details can still be typed in by hand.
        </div>
      )}

      <div className="asset-picker__grid">
        <AutoComplete
          className="asset-picker__search"
          options={options}
          value={search ?? current.registration ?? ''}
          disabled={disabled}
          onChange={handleSearchChange}
          onSelect={handleSelect}
          onPopupScroll={handleDropdownScroll}
          notFoundContent={emptyText()}
          filterOption={false}
        >
          <Input placeholder="Search registration, fleet, VIN, make or model" />
        </AutoComplete>

        <Input
          placeholder="Fleet No."
          value={current.fleetNo ?? ''}
          disabled={disabled}
          onChange={(event) => patch({ fleetNo: event.target.value })}
        />
        <Input
          placeholder="Make"
          value={current.make ?? ''}
          disabled={disabled}
          onChange={(event) => patch({ make: event.target.value })}
        />
        <Input
          placeholder="Model"
          value={current.model ?? ''}
          disabled={disabled}
          onChange={(event) => patch({ model: event.target.value })}
        />
        <InputNumber
          placeholder="Odometer"
          style={{ width: '100%' }}
          value={current.odometer ?? undefined}
          disabled={disabled}
          onChange={(next) => patch({ odometer: next ?? null })}
        />
        <InputNumber
          placeholder="Hours"
          style={{ width: '100%' }}
          value={current.hours ?? undefined}
          disabled={disabled}
          onChange={(next) => patch({ hours: next ?? null })}
        />
        <Input
          placeholder="Driver licence"
          value={current.driverLicense ?? ''}
          disabled={disabled}
          onChange={(event) => patch({ driverLicense: event.target.value })}
        />
      </div>
    </div>
  );
};

export default AssetPickerField;
